/**
 * Score a value against a search query for relevance-based sorting.
 *
 * This is the shared search-matching backbone used across the app: it is the
 * default filter for the cmdk `Command` wrapper (so every command palette /
 * combobox built on it inherits this behaviour) and the engine behind
 * `filterAndSortBySearch` for manual client-side lists.
 *
 * Beyond plain substring matching it adds the flexibility users expect from an
 * autocomplete:
 *  - **Multi-token queries** — the query is split on whitespace and each token
 *    is matched independently, in any order. This lets a value be found by a
 *    mixture of words drawn from different fields (e.g. a module name + a page
 *    name combined into one searchable string).
 *  - **Partial multi-token matches** — a value that matches only SOME of the
 *    typed words is still surfaced, ranked strictly below every full match.
 *    Typing "freight services" must not hide an account named "Freight,
 *    Customs & Duties"; showing nothing is worse than showing a good partial.
 *  - **Singular/plural folding** — "category" matches "Categories" and vice
 *    versa via lightweight English stemming.
 *  - **Small typos** — a token within a short edit distance of a value word
 *    still matches (e.g. "messging" → "Messaging").
 *
 * Returns a relevance score where higher is more relevant and `0` means the
 * item should be hidden. Exact matches always outrank stem matches, which
 * always outrank fuzzy matches:
 *  0    — no match (hide)
 *  ~0.5 — typo / fuzzy match
 *  ~0.7-0.9 — singular/plural (stem) match
 *  1    — substring/contains match (e.g. "Pro*cu*rement" for "cu")
 *  2    — non-first word prefix match (e.g. "File *Man*agement" for "man")
 *  3    — first word prefix match (e.g. "*Mes*saging" for "mes")
 *
 * For a multi-token query where EVERY token matches, the score is the average
 * of the per-token scores, so the tiers above still describe the single-token
 * (and best-case) behaviour. A partial match instead lands in a separate band
 * below `PARTIAL_MATCH_CEILING` — see {@link partialScore}.
 */

/** Tokens shorter than this are never fuzzy-matched (too noisy). */
const FUZZY_MIN_LEN = 4;

/** The highest score any single token can earn (first-word prefix). */
const MAX_TOKEN_SCORE = 3;

/**
 * Partial matches are squeezed into `(0, PARTIAL_MATCH_CEILING)`, which sits
 * strictly below the weakest possible FULL match — a query whose every token
 * only fuzzy-prefix-matches averages 0.45. So a value matching all the typed
 * words always outranks one matching some of them, whatever the tiers involved.
 *
 * It also sits below `moduleSearch`'s 0.7 strong-match tier, so global
 * navigation search treats partials as a fallback rather than flooding the
 * results whenever something matched properly.
 */
export const PARTIAL_MATCH_CEILING = 0.4;

/**
 * A partial match that misses the FIRST word the user typed is halved. The
 * opening word is the one the user is most confident about — "freight
 * services" should rank the freight rows above the services ones.
 */
const FIRST_TOKEN_MISS_PENALTY = 0.5;

/**
 * Reduce an English word to a rough singular stem so plural and singular forms
 * compare equal. Deliberately small and rule-based — not a full stemmer — but
 * enough for entity/page/module names ("categories"→"category",
 * "boxes"→"box", "products"→"product"; "address" stays "address").
 */
export function singularize(word: string): string {
  if (word.length <= 3) return word;
  if (word.endsWith('ies') && word.length > 4) return `${word.slice(0, -3)}y`;
  if (/(ses|xes|zes|ches|shes)$/.test(word)) return word.slice(0, -2);
  // Don't strip a trailing -s from words that aren't English plurals — doing so
  // would produce spurious stem matches. Covers -ss (address, boss), -us
  // (status, focus, bonus, porous), and -is/-sis/-xis (analysis, axis, basis).
  if (/(ss|us|is)$/.test(word)) return word;
  if (word.endsWith('s')) return word.slice(0, -1);
  return word;
}

/** Levenshtein edit distance between two strings (iterative two-row DP). */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  let curr = new Array<number>(b.length + 1);

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1, // deletion
        curr[j - 1] + 1, // insertion
        prev[j - 1] + cost, // substitution
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

/** Score a single query token against a pre-tokenized value. */
function tokenScore(
  valueLower: string,
  valueWords: string[],
  valueStems: string[],
  token: string,
): number {
  // Exact tiers (highest priority — preserves the historic 3/2/1 contract).
  if (valueWords[0]?.startsWith(token)) return 3;
  for (let i = 1; i < valueWords.length; i++) {
    if (valueWords[i].startsWith(token)) return 2;
  }
  if (valueLower.includes(token)) return 1;

  // Singular/plural (stem) tiers — ranked just below exact substring matches.
  const stem = singularize(token);
  if (valueStems[0]?.startsWith(stem)) return 0.9;
  for (let i = 1; i < valueStems.length; i++) {
    if (valueStems[i].startsWith(stem)) return 0.8;
  }
  if (valueStems.some((w) => w.includes(stem))) return 0.7;

  // Typo / fuzzy tier — lowest positive score, conservative thresholds.
  if (token.length >= FUZZY_MIN_LEN) {
    const maxDist = token.length >= 8 ? 2 : 1;
    for (const w of valueWords) {
      if (w.length < FUZZY_MIN_LEN) continue;
      if (Math.abs(w.length - token.length) <= maxDist && levenshtein(w, token) <= maxDist) {
        return 0.5;
      }
      // Fuzzy prefix: the start of a longer word is close to the token.
      if (w.length > token.length && levenshtein(w.slice(0, token.length), token) <= maxDist) {
        return 0.45;
      }
    }
  }

  return 0;
}

export function searchScore(value: string, search: string, keywords?: string[]): number {
  const searchLower = search.toLowerCase().trim();
  if (!searchLower) return 1; // show everything when search is empty

  // cmdk passes optional keywords as a third arg — fold them into the haystack
  // so items can be found by aliases they declare.
  const haystack = keywords && keywords.length > 0 ? `${value} ${keywords.join(' ')}` : value;
  const valueLower = haystack.toLowerCase();
  const valueWords = valueLower.split(/\s+/).filter(Boolean);
  if (valueWords.length === 0) return 0;
  const valueStems = valueWords.map(singularize);

  // Score every query token independently. When all of them match, the overall
  // score is their average so values that match more strongly rank higher.
  // Note: scores can exceed cmdk's documented [0,1] range (the historic 3/2/1
  // tiers predate this util being the cmdk filter); cmdk sorts by raw numeric
  // comparison so larger-is-better holds. Keep returns monotonic, not clamped.
  const tokens = searchLower.split(/\s+/).filter(Boolean);
  let matchedTotal = 0;
  let matchedCount = 0;
  let firstTokenMatched = false;
  for (let i = 0; i < tokens.length; i++) {
    const score = tokenScore(valueLower, valueWords, valueStems, tokens[i]);
    if (score === 0) continue;
    matchedTotal += score;
    matchedCount++;
    if (i === 0) firstTokenMatched = true;
  }

  if (matchedCount === 0) return 0;
  if (matchedCount === tokens.length) return matchedTotal / tokens.length;
  return partialScore(matchedTotal / matchedCount, matchedCount / tokens.length, firstTokenMatched);
}

/**
 * Rank a value that matched only some of the typed words.
 *
 * The rank is the strength of the words that DID match, weighted by how many
 * of the typed words that was, and halved when the first typed word was not
 * among them. The whole band is scaled under {@link PARTIAL_MATCH_CEILING} so
 * it can never overtake a full match.
 */
function partialScore(
  averageMatchedScore: number,
  coverage: number,
  firstTokenMatched: boolean,
): number {
  return (
    PARTIAL_MATCH_CEILING *
    coverage *
    (averageMatchedScore / MAX_TOKEN_SCORE) *
    (firstTokenMatched ? 1 : FIRST_TOKEN_MISS_PENALTY)
  );
}

/**
 * Filter and sort an array of items using prefix-prioritized search scoring.
 *
 * @param items - The items to filter
 * @param search - The search query string
 * @param getText - Function to extract the searchable text from an item
 * @returns Filtered and sorted array (prefix matches first, then substring matches)
 */
export function filterAndSortBySearch<T>(
  items: T[],
  search: string,
  getText: (item: T) => string,
): T[] {
  if (!search.trim()) return items;

  const scored = items
    .map((item) => ({ item, score: searchScore(getText(item), search) }))
    .filter(({ score }) => score > 0);

  scored.sort((a, b) => b.score - a.score);

  return scored.map(({ item }) => item);
}

/**
 * A match at the singular/plural (stem) tier or better — real evidence the value
 * contains what was typed, as opposed to the typo/partial band below it.
 *
 * Shared because two surfaces need the same cut-off between "matched" and "might
 * have matched": global navigation search ranks modules and pages with it, and
 * {@link filterAndSortByPrioritizedSearch} uses it as the bar a SECONDARY field
 * must clear on its own.
 */
export const STRONG_MATCH = 0.7;

/**
 * Within the "subject matched" band, how much the subject's own score outweighs
 * the body's. Ten, so the body can only ever break a tie between rows whose
 * subjects scored the same tier — never reorder two different subject tiers.
 *
 * It is NOT what separates a subject hit from a body-only one: a weight cannot
 * do that job, because `searchScore`'s partial band is unbounded BELOW (a
 * one-of-three-token subject hit that misses the first token scores ~0.067,
 * which × 10 still loses to a body-only 2). The separation is the sort's first
 * key instead — see {@link filterAndSortByPrioritizedSearch}.
 */
const PRIMARY_FIELD_WEIGHT = 10;

/** Field extractors for {@link filterAndSortByPrioritizedSearch}. */
export interface PrioritizedSearchFields<T> {
  /** The subject line — a notification's title, a conversation's name. */
  primary: (item: T) => string;
  /** Supporting body text — a notification's body, a message preview. */
  secondary?: (item: T) => string;
}

/**
 * Filter and rank a list against a query, giving priority to a SUBJECT match.
 *
 * Two fields, scored separately rather than concatenated, because concatenating
 * them makes a hit on a long body indistinguishable from a hit on the title:
 *
 *  - **Ranking** — two keys, in order: did the TITLE match at all, then
 *    `primary × 10 + secondary`. The first key is what makes "any title hit,
 *    however weak, outranks every body-only hit" actually true; arithmetic alone
 *    cannot, because `searchScore`'s partial band runs arbitrarily close to zero
 *    (a query whose first token misses a three-token title scores ~0.067, and no
 *    finite weight lifts that above a body-only 2). Inside each band the score
 *    orders the rows, so the body still breaks ties between equal titles.
 *  - **Admission** — a row qualifies when its TITLE matched at all (a typo or a
 *    partial on the subject is still the row the reader meant), or when its BODY
 *    matched at {@link STRONG_MATCH} or better. What that excludes is the noise
 *    band: fuzzy or partial evidence drawn from body prose, which is how
 *    searching a person's name used to drag in every row that merely mentions a
 *    similar-looking word (see `moduleSearch`'s `isDecentPageMatch`, the same
 *    rule applied to nav pages).
 *
 * Typo tolerance, plural folding and multi-token queries all come from
 * {@link searchScore} unchanged.
 *
 * An empty query returns the items untouched, in their original order.
 */
export function filterAndSortByPrioritizedSearch<T>(
  items: T[],
  search: string,
  fields: PrioritizedSearchFields<T>,
): T[] {
  if (!search.trim()) return items;

  const scored: { item: T; subjectMatched: boolean; rank: number }[] = [];
  for (const item of items) {
    const primaryScore = searchScore(fields.primary(item), search);
    const secondaryText = fields.secondary?.(item) ?? '';
    const secondaryScore = secondaryText ? searchScore(secondaryText, search) : 0;
    if (primaryScore === 0 && secondaryScore < STRONG_MATCH) continue;
    scored.push({
      item,
      subjectMatched: primaryScore > 0,
      rank: primaryScore * PRIMARY_FIELD_WEIGHT + secondaryScore,
    });
  }

  // Array.prototype.sort is stable, so equally-ranked rows keep the order the
  // caller gave them — newest-first for an inbox.
  scored.sort(
    (a, b) => Number(b.subjectMatched) - Number(a.subjectMatched) || b.rank - a.rank,
  );
  return scored.map(({ item }) => item);
}
